/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from 'react';
import { Formik, Form as FormikForm } from 'formik';
import { useNavigate, useParams } from 'react-router-dom';
import moment from 'moment';
import CloseIcon from '@mui/icons-material/Close';
import NInput from '../../components/inputs/NInput';
import NButton from '../../components/inputs/NButton';
import { addProjectValidation } from '../../validations/validation';
import NDropdown from '../../components/inputs/NDropdown';
import NDatePicker from '../../components/inputs/NDatePicker';
import useProjectAPI from '../../hooks/network/useProjectAPI';
import { errorToaster, successToaster } from '../../components/toaster/Toaster';
import { ResponseMessage } from '../../constants/Constants';
import { RouteEnums } from '../../Routes/RoutesConstant';
import { useFormHook } from '../../hooks/custom-hooks/useFormHook';
import useUserAPI from '../../hooks/network/useUserAPI';
import NSearchableDropDown from '../../components/inputs/NSearchableDropdown';
import useWidgetAPI from '../../hooks/network/useWidgetAPI';

const formInitialValues = {
  customer: '',
  projectName: '',
  startDate: '',
  endDate: '',
  personInCharge: '',
  personInChargeEmail: '',
  accountManager: '',
  accountManagerEmail: '',
  sapId: '',
  notes: '',
  maxUserCount: '',
};

function AddProject() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { getProject, addProject, updateProject, isLoading } = useProjectAPI();
  const { getDropDownOptions } = useUserAPI();
  const { formRef, formHeight } = useFormHook();
  const { getDropDownOptions: getProjectDropdownOptions } = useWidgetAPI();

  const [formValues, setFormValues] = useState(formInitialValues);
  const [customerOptions, setCustomerOptions] = useState();
  const [projectOptions, setProjectOptions] = useState();

  useEffect(() => {
    fetchOptions();
  }, []);

  useEffect(() => {
    if (projectId) {
      fetchProjectDetails();
    }
  }, [projectId]);

  const fetchOptions = async () => {
    try {
      getDropDownOptions().then((data) => {
        setCustomerOptions(data);
      });
      getProjectDropdownOptions().then((data) => {
        setProjectOptions(data);
      });
    } catch {}
  };

  const fetchProjectDetails = async () => {
    try {
      const response = await getProject(projectId);
      setFormValues({
        customer: response?.customer?.toString(),
        projectName: response?.projectName,
        startDate: moment(response?.startDate)?.toDate() || '',
        endDate: moment(response?.endDate)?.toDate() || '',
        personInCharge: response?.personInCharge || '',
        personInChargeEmail: response?.personInChargeEmail || '',
        accountManager: response?.accountManager || '',
        accountManagerEmail: response?.accountManagerEmail || '',
        sapId: response?.sapid,
        notes: response?.notes || '',
        maxUserCount: response?.maxUserCount,
      });
    } catch {}
  };

  const handleFormSubmit = async (values, formikBag) => {
    try {
      if (projectId) {
        const response = await updateProject(projectId, {
          ...values,
          startDate: moment(values?.startDate)?.toISOString(true),
          endDate: moment(values?.endDate)?.toISOString(true),
        });
        if (response?.success) {
          successToaster(response?.message || 'Project Updated');
          navigate(RouteEnums.PROJECT);
        } else errorToaster(response?.message || response?.error || ResponseMessage.ERROR_DEFAULT);
      } else {
        const response = await addProject(values);
        if (response?.success) {
          formikBag.resetForm();
          setFormValues(formInitialValues);
          successToaster(response?.message || 'New Project Created');
          navigate(RouteEnums.PROJECT);
        } else errorToaster(response?.message || response?.error || ResponseMessage.ERROR_DEFAULT);
      }
    } catch {}
  };

  const optionValueCustomer = (val) => {
    const selectedObj = customerOptions?.data?.customers?.find(
      (o) => o?.id?.toString() === val?.toString(),
    );
    return { label: selectedObj?.customerName, value: selectedObj?.id?.toString() };
  };

  return (
    <>
      <div className='nmobile-form-blur' />
      <div
        style={{
          height:
            window.innerWidth < 1180
              ? `min(calc(100vh - 150px), calc(100vh - ${formHeight}px))`
              : '100%',
        }}
        className='nform-wrapper'
      >
        <div className='nform-container'>
          {/* Mobile Header Heading */}
          <div className='nmobile-title'>
            <h2 className='app-heading h2-style'>{projectId ? 'Edit' : 'Add'} Project</h2>
            <div onClick={() => navigate(RouteEnums.PROJECT)}>
              <CloseIcon />
            </div>
          </div>

          <div className='nform'>
            <div ref={formRef}>
              <Formik
                initialValues={formValues}
                validationSchema={addProjectValidation}
                onSubmit={(values, formikBag) => {
                  handleFormSubmit(values, formikBag);
                }}
                enableReinitialize
              >
                {({
                  touched,
                  values,
                  errors,
                  handleSubmit,
                  handleChange,
                  handleBlur,
                  setFieldValue,
                  setFieldTouched,
                }) => (
                  <FormikForm className='' autoComplete='off' onSubmit={handleSubmit}>
                    {/* Desktop buttons */}
                    <div className='ntitle-nAction-button'>
                      <h2 className='app-heading h2-style'>
                        {projectId ? 'Edit' : 'Create'} Project
                      </h2>
                      <div className='naction-btns'>
                        <div>
                          <NButton variant='Noutline' onClick={() => navigate('/projects/')}>
                            Cancel
                          </NButton>
                        </div>
                        <div>
                          <NButton
                            isLoading={isLoading}
                            onClick={handleSubmit}
                            variant='Ncontained'
                          >
                            {projectId ? 'Update' : 'Save'} Project
                          </NButton>
                        </div>
                      </div>
                    </div>

                    {/* Common form */}
                    <div className='nform-fields'>
                      <div>
                        <NSearchableDropDown
                          label='Customer Name*'
                          name='customer'
                          id='customer'
                          value={optionValueCustomer(values?.customer)}
                          onBlur={handleBlur}
                          onChange={(e, val) => {
                            if (val?.value) {
                              setFieldValue('customer', val?.value);
                            } else {
                              setFieldValue('customer', '');
                            }
                            setFieldValue('projectName', '');
                          }}
                          error={errors.customer && touched.customer}
                          helperText={errors.customer && touched.customer ? errors.customer : ''}
                          className='w-full'
                          options={
                            customerOptions?.data?.customers?.map((o) => ({
                              label: o?.customerName,
                              value: o?.id?.toString(),
                            })) || []
                          }
                        />
                      </div>
                      <div>
                        <NSearchableDropDown
                          label='Project*'
                          name='projectName'
                          id='projectName'
                          value={(() => {
                            const sapId = customerOptions?.data?.customers?.find(
                              (f) => f?.id?.toString() === values?.customer?.toString(),
                            )?.sapid;

                            const pOptions = projectOptions
                              ?.find((c) => c?.customerID === sapId)
                              ?.projects?.find((p) => p?.name === values?.projectName);

                            return {
                              label: pOptions?.name,
                              value: pOptions?.name,
                              sapid: pOptions?.id,
                            };
                          })()}
                          onChange={(e, selected) => {
                            if (selected?.value) {
                              setFieldValue('projectName', selected?.value);
                              setFieldValue('sapId', selected?.sapid);
                            } else {
                              setFieldValue('projectName', '');
                              setFieldValue('sapId', '');
                            }
                          }}
                          onBlur={handleBlur}
                          error={errors.projectName && touched.projectName}
                          helperText={
                            errors.projectName && touched.projectName ? errors.projectName : ''
                          }
                          className='w-full'
                          options={(() => {
                            const sapId = customerOptions?.data?.customers?.find(
                              (f) => f?.id?.toString() === values?.customer?.toString(),
                            )?.sapid;

                            const options =
                              projectOptions
                                ?.find((c) => c?.customerID === sapId)
                                ?.projects?.map((p) => ({
                                  label: p?.name,
                                  value: p?.name,
                                  sapid: p?.id,
                                })) || [];

                            return options;
                          })()}
                        />
                      </div>
                      <div className='grid grid-cols-2 gap-4 maclg:grid-cols-2 maclg:gap-y-1'>
                        <div>
                          <NDatePicker
                            onChange={(e) => setFieldValue('startDate', e || undefined)}
                            onBlur={() => setFieldTouched('startDate', true)}
                            value={values?.startDate}
                            showYearDropdown
                            showMonthDropdown
                            label='Project Start Date*'
                            className='w-full'
                            name='startDate'
                            error={errors.startDate && touched.startDate}
                            helperText={
                              errors.startDate && touched.startDate ? errors.startDate : ''
                            }
                            placeholder='DD/MM/YYYY'
                            dateFormat='dd/MM/yyyy'
                            onCalendarClose={() =>
                              !values?.startDate && setFieldTouched('startDate', true)
                            }
                          />
                        </div>
                        <div>
                          <NDatePicker
                            onChange={(e) => setFieldValue('endDate', e || undefined)}
                            onBlur={() => setFieldTouched('endDate', true)}
                            value={values?.endDate}
                            showYearDropdown
                            showMonthDropdown
                            label='Project End Date*'
                            className='w-full'
                            name='endDate'
                            error={errors.endDate && touched.endDate}
                            helperText={errors.endDate && touched.endDate ? errors.endDate : ''}
                            placeholder='DD/MM/YYYY'
                            dateFormat='dd/MM/yyyy'
                            onCalendarClose={() =>
                              !values?.endDate && setFieldTouched('endDate', true)
                            }
                          />
                        </div>
                      </div>
                      <div className='w-full'>
                        <NInput
                          name='personInCharge'
                          value={values.personInCharge}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={errors.personInCharge && touched.personInCharge}
                          helperText={
                            errors.personInCharge && touched.personInCharge
                              ? errors.personInCharge
                              : ''
                          }
                          className='w-full'
                          label='Person In-Charge'
                          placeholder='Enter Person In-Charge Name'
                          type='text'
                        />
                      </div>
                      <div className='w-full'>
                        <NInput
                          name='personInChargeEmail'
                          value={values.personInChargeEmail}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={errors.personInChargeEmail && touched.personInChargeEmail}
                          helperText={
                            errors.personInChargeEmail && touched.personInChargeEmail
                              ? errors.personInChargeEmail
                              : ''
                          }
                          className='w-full'
                          label='Person In-Charge Email ID'
                          placeholder='Enter Person In-Charge Email ID'
                          type='text'
                        />
                      </div>
                      <div className='w-full'>
                        <NInput
                          name='accountManager'
                          value={values.accountManager}
                          onChange={(e) => handleChange(e)}
                          onBlur={handleBlur}
                          error={errors.accountManager && touched.accountManager}
                          helperText={
                            errors.accountManager && touched.accountManager
                              ? errors.accountManager
                              : ''
                          }
                          className='w-full'
                          label='Account Manager'
                          placeholder='Enter Account Manager Name'
                          type='text'
                        />
                      </div>
                      <div className='w-full'>
                        <NInput
                          name='accountManagerEmail'
                          value={values.accountManagerEmail}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={errors.accountManagerEmail && touched.accountManagerEmail}
                          helperText={
                            errors.accountManagerEmail && touched.accountManagerEmail
                              ? errors.accountManagerEmail
                              : ''
                          }
                          className='w-full'
                          label='Account Manager Email'
                          placeholder='Enter Account Manager Email'
                          type='text'
                        />
                      </div>
                      <div className='w-full'>
                        <div className='w-full'>
                          <NInput
                            required
                            name='sapId'
                            value={values.sapId}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={errors.sapId && touched.sapId}
                            helperText={errors.sapId && touched.sapId ? errors.sapId : ''}
                            className='w-full'
                            label='Project (SAP ID)'
                            placeholder='Enter Project SAP ID'
                            type='text'
                          />
                        </div>
                      </div>
                      <div className='w-full'>
                        <NInput
                          name='notes'
                          value={values.notes}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={errors.notes && touched.notes}
                          helperText={errors.notes && touched.notes ? errors.notes : ''}
                          className='w-full'
                          label='Notes'
                          placeholder='Enter Notes'
                          type='text'
                          multiline
                          rows={7}
                        />
                      </div>
                      <div className='w-full'>
                        <NDropdown
                          required
                          label='Number of Users*'
                          name='maxUserCount'
                          value={values.maxUserCount}
                          onChange={(e) => handleChange(e)}
                          onBlur={handleBlur}
                          error={errors.maxUserCount && touched.maxUserCount}
                          helperText={
                            errors.maxUserCount && touched.maxUserCount ? errors.maxUserCount : ''
                          }
                          className='w-full'
                          type='text'
                          options={[
                            ...Array.from({ length: 10 }, (_, i) => i + 1).map((o) => ({
                              label: o?.toString(),
                              value: o,
                            })),
                            { label: 'Unlimited', value: 0 },
                          ]}
                        />
                      </div>
                    </div>

                    {/* Mobile Buttons */}
                    <div className='nmobile-action-btn'>
                      <div>
                        <NButton isLoading={isLoading} onClick={handleSubmit} variant='Ncontained'>
                          {projectId ? 'Update' : 'Save'} Project
                        </NButton>
                      </div>
                    </div>
                  </FormikForm>
                )}
              </Formik>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default AddProject;
